import { usePlan } from './chunk-BS6JEKBZ.js';
import { useFeatureFlags } from './chunk-WJJBT3ZR.js';
import { useOptionalSession } from './chunk-7QYYSJGW.js';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './chunk-Z2NUSCHM.js';
import { Switch } from './chunk-UKAZWVJA.js';
import { Separator } from './chunk-NCCYLVD3.js';
import { writeClipboardText } from './chunk-K4S4BS2H.js';
import { Button } from './chunk-PLP233UC.js';
import * as React3 from 'react';
import { Check, Copy, Bug, X } from 'lucide-react';
import { isDebugEnabled } from '@authe/fe-commons/config';
import { clearAuthCache } from '@authe/fe-commons/session';
import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { formatShare } from '@authe/fe-commons/value';
import { formatDuration, formatTimestamp, formatRelativeTime } from '@authe/fe-commons/time';
import { debugResourceSink } from '@authe/fe-commons/api';

function useCopy(text, ms = 1e3) {
  const [copied, setCopied] = React3.useState(false);
  const copy = React3.useCallback(() => {
    void writeClipboardText(text).then((ok) => {
      if (!ok) return;
      setCopied(true);
      window.setTimeout(() => setCopied(false), ms);
    });
  }, [text, ms]);
  return [copied, copy];
}
function CopyableValue({ value }) {
  const [copied, copy] = useCopy(value);
  return /* @__PURE__ */ jsxs(
    "button",
    {
      type: "button",
      onClick: copy,
      title: `Copy: ${value}`,
      className: "inline-flex max-w-full items-center gap-1 font-mono hover:text-foreground",
      children: [
        /* @__PURE__ */ jsx("span", { className: "truncate", children: value }),
        copied ? /* @__PURE__ */ jsx(Check, { className: "size-3 shrink-0 text-success", "aria-hidden": true }) : /* @__PURE__ */ jsx(Copy, { className: "size-3 shrink-0 opacity-50", "aria-hidden": true })
      ]
    }
  );
}
function CopyTextButton({
  text,
  label = "Copy"
}) {
  const [copied, copy] = useCopy(text, 1200);
  return /* @__PURE__ */ jsx(
    Button,
    {
      type: "button",
      size: "sm",
      variant: "ghost",
      className: "text-xs",
      onClick: copy,
      disabled: !text,
      children: copied ? "Copied" : label
    }
  );
}
function toUnixSeconds(value) {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value > 1e11 ? value / 1e3 : value;
  }
  if (typeof value === "string" && value !== "") {
    const ms = Date.parse(value);
    if (!Number.isNaN(ms)) return ms / 1e3;
  }
  return null;
}
function formatBytes(bytes) {
  if (!Number.isFinite(bytes)) return String(bytes);
  if (Math.abs(bytes) < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let value = bytes / 1024;
  let unit = 0;
  while (Math.abs(value) >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(Math.abs(value) < 100 ? 1 : 0)} ${units[unit]}`;
}
function isAbsent(value) {
  return value === null || value === void 0 || value === "";
}
function isEmptyContainer(value) {
  if (Array.isArray(value)) return value.length === 0;
  if (value && typeof value === "object") return Object.keys(value).length === 0;
  return false;
}
function FieldValue({ field }) {
  const { value, kind } = field;
  if (isAbsent(value)) return /* @__PURE__ */ jsx("span", { className: "text-muted-foreground/70", children: "\u2014" });
  switch (kind) {
    case "id":
      return /* @__PURE__ */ jsx(CopyableValue, { value: String(value) });
    case "timestamp": {
      const seconds = toUnixSeconds(value);
      if (seconds === null) return /* @__PURE__ */ jsx(Fragment, { children: String(value) });
      return /* @__PURE__ */ jsxs("span", { className: "inline-flex flex-col items-end leading-tight", children: [
        /* @__PURE__ */ jsx("span", { children: formatTimestamp(seconds) }),
        /* @__PURE__ */ jsx("span", { className: "text-3xs text-muted-foreground", children: formatRelativeTime(seconds) })
      ] });
    }
    case "duration_ms":
      return /* @__PURE__ */ jsx(Fragment, { children: typeof value === "number" ? formatDuration(value) : String(value) });
    case "bytes":
      return /* @__PURE__ */ jsx(Fragment, { children: typeof value === "number" ? formatBytes(value) : String(value) });
    case "ratio":
      return /* @__PURE__ */ jsx(Fragment, { children: typeof value === "number" ? formatShare(value) : String(value) });
    case "json": {
      if (isEmptyContainer(value)) return /* @__PURE__ */ jsx("span", { className: "text-muted-foreground/70", children: "none" });
      return /* @__PURE__ */ jsx("pre", { className: "max-h-32 max-w-full overflow-auto whitespace-pre-wrap break-all rounded-sm border bg-muted/40 p-1.5 text-left text-3xs leading-relaxed", children: JSON.stringify(value, null, 2) });
    }
    default:
      return /* @__PURE__ */ jsx(Fragment, { children: String(value) });
  }
}
function DebugFieldRow({ field }) {
  const absent = isAbsent(field.value);
  return /* @__PURE__ */ jsxs("div", { className: "flex items-start justify-between gap-3 text-xs", children: [
    /* @__PURE__ */ jsx("span", { className: "shrink-0 text-muted-foreground", title: field.hint, children: field.label }),
    /* @__PURE__ */ jsxs("span", { className: "min-w-0 max-w-[62%] break-words text-right font-mono text-foreground", children: [
      /* @__PURE__ */ jsx(FieldValue, { field }),
      absent && field.hint && /* @__PURE__ */ jsx("span", { className: "block text-3xs font-sans italic text-muted-foreground/80", children: field.hint })
    ] })
  ] });
}
function DebugFieldGroup({ group }) {
  if (group.fields.length === 0) return null;
  return /* @__PURE__ */ jsxs("div", { className: "space-y-1.5", children: [
    /* @__PURE__ */ jsx("p", { className: "text-2xs font-medium uppercase tracking-wide text-muted-foreground/80", children: group.label }),
    group.fields.map((field, i) => /* @__PURE__ */ jsx(DebugFieldRow, { field }, `${field.key}-${i}`))
  ] });
}
function DebugFieldGroups({ groups }) {
  const nonEmpty = groups.filter((g) => g.fields.length > 0);
  if (nonEmpty.length === 0) return null;
  return /* @__PURE__ */ jsx("div", { className: "space-y-3", children: nonEmpty.map((group, i) => /* @__PURE__ */ jsxs(React3.Fragment, { children: [
    i > 0 && /* @__PURE__ */ jsx(Separator, {}),
    /* @__PURE__ */ jsx(DebugFieldGroup, { group })
  ] }, `${group.key}-${i}`)) });
}
var DebugResourceRegistry = class {
  entries = /* @__PURE__ */ new Map();
  listeners = /* @__PURE__ */ new Set();
  snapshot = [];
  /** Publish (or, with `null`, withdraw) the record under `key`. */
  publish(key, entry) {
    if (entry === null) {
      if (!this.entries.delete(key)) return;
    } else {
      this.entries.set(key, entry);
    }
    this.snapshot = [...this.entries.values()];
    for (const listener of this.listeners) listener();
  }
  /** Everything published, in publication order. Stable between changes. */
  list = () => this.snapshot;
  subscribe = (listener) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };
};
var DebugResourcesContext = React3.createContext(null);
function DebugResourceProvider({
  children,
  registry,
  routeKey
}) {
  const [fallback] = React3.useState(() => new DebugResourceRegistry());
  const value = registry ?? fallback;
  React3.useEffect(() => {
    if (routeKey === void 0) return;
    debugResourceSink.clear();
  }, [routeKey]);
  return /* @__PURE__ */ jsx(DebugResourcesContext.Provider, { value, children });
}
function useHookResources() {
  const registry = React3.useContext(DebugResourcesContext);
  const subscribe = registry?.subscribe ?? noopSubscribe;
  const list = registry?.list ?? emptyList;
  return React3.useSyncExternalStore(subscribe, list, list);
}
function useCapturedResources() {
  return React3.useSyncExternalStore(
    debugResourceSink.subscribe,
    debugResourceSink.list,
    emptyCaptured
  );
}
function useDebugResources() {
  const hookEntries = useHookResources();
  const captured = useCapturedResources();
  return React3.useMemo(
    () => mergeDebugResources(captured, hookEntries),
    [captured, hookEntries]
  );
}
function mergeDebugResources(captured, hookEntries) {
  const byKind = /* @__PURE__ */ new Map();
  for (const entry of captured) byKind.set(entry.resource, withCaptureNote(entry));
  for (const entry of hookEntries) byKind.set(entry.resource, entry);
  return [...byKind.values()];
}
function withCaptureNote(entry) {
  const { path, at, ...rest } = entry;
  return {
    ...rest,
    groups: [
      ...rest.groups,
      {
        key: "captured",
        label: "Captured",
        fields: [
          { key: "request", label: "From request", value: path, kind: "text" },
          {
            key: "at",
            label: "Captured",
            value: Math.floor(at / 1e3),
            kind: "timestamp",
            hint: "The response this was read off. Nothing re-reads it on its own."
          }
        ]
      }
    ]
  };
}
function useDebugResource(entry) {
  const registry = React3.useContext(DebugResourcesContext);
  const key = React3.useId();
  const enabled = registry !== null && isDebugEnabled();
  const signature = enabled && entry ? entrySignature(entry) : null;
  const [published, setPublished] = React3.useState(
    signature === null ? null : entry ?? null
  );
  const [publishedSignature, setPublishedSignature] = React3.useState(signature);
  if (signature !== publishedSignature) {
    setPublishedSignature(signature);
    setPublished(signature === null ? null : entry ?? null);
  }
  React3.useEffect(() => {
    if (!registry || !published) return void 0;
    registry.publish(key, published);
    return () => registry.publish(key, null);
  }, [registry, published, key]);
}
function resourceLabel(entry) {
  if (entry.label) return entry.label;
  const words = entry.resource.replace(/[_-]+/g, " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}
function entrySignature(entry) {
  try {
    return JSON.stringify(entry) ?? structuralDigest(entry);
  } catch {
    return structuralDigest(entry);
  }
}
function structuralDigest(entry) {
  const groups = entry.groups.map((g) => `${g.key}:${g.fields.map((f) => f.key).join(",")}`).join("|");
  return `${entry.resource}#${entry.id ?? ""}#${groups}`;
}
var EMPTY = [];
var emptyList = () => EMPTY;
var EMPTY_CAPTURED = [];
var emptyCaptured = () => EMPTY_CAPTURED;
var noopSubscribe = () => () => {
};
function ScopesSection({ scopes }) {
  const [open, setOpen] = React3.useState(false);
  if (scopes.length === 0) {
    return /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: "No scopes on token." });
  }
  return /* @__PURE__ */ jsxs("div", { className: "space-y-2", children: [
    /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
      /* @__PURE__ */ jsxs(
        Button,
        {
          type: "button",
          size: "sm",
          variant: "outline",
          className: "text-xs",
          "aria-expanded": open,
          onClick: () => setOpen((o) => !o),
          children: [
            open ? "Hide" : "Show",
            " ",
            scopes.length,
            " scope",
            scopes.length === 1 ? "" : "s"
          ]
        }
      ),
      /* @__PURE__ */ jsx(CopyTextButton, { text: scopes.join("\n") })
    ] }),
    open && /* @__PURE__ */ jsx("ul", { className: "max-h-40 space-y-0.5 overflow-auto rounded-sm border bg-muted/40 p-2 font-mono text-2xs leading-relaxed", children: scopes.map((s) => /* @__PURE__ */ jsx("li", { className: "break-all", children: s }, s)) })
  ] });
}
function PlanSection() {
  const live = usePlan().plan?.id ?? null;
  const minted = useOptionalSession()?.session?.claims?.plan_id ?? null;
  if (!live && !minted) return null;
  const stale = Boolean(live && minted && live !== minted);
  return /* @__PURE__ */ jsxs("div", { className: "space-y-1.5", children: [
    /* @__PURE__ */ jsx(DebugRowView, { label: "plan (live)", value: live ?? "unknown", copyable: Boolean(live) }),
    /* @__PURE__ */ jsx(
      DebugRowView,
      {
        label: "plan (token)",
        value: minted ?? "not stamped",
        copyable: Boolean(minted)
      }
    ),
    stale && /* @__PURE__ */ jsx("p", { className: "text-3xs italic text-muted-foreground", children: "This session predates a plan change; re-minting the token." })
  ] });
}
function RawJwtSection({
  getAccessToken
}) {
  const [token, setToken] = React3.useState(null);
  const [loading, setLoading] = React3.useState(false);
  const shown = token !== null;
  const reveal = React3.useCallback(() => {
    setLoading(true);
    void getAccessToken().then((t) => setToken(t ?? "")).catch((error) => setToken(`<unavailable: ${String(error)}>`)).finally(() => setLoading(false));
  }, [getAccessToken]);
  return /* @__PURE__ */ jsxs("div", { className: "space-y-2", children: [
    /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
      /* @__PURE__ */ jsx(
        Button,
        {
          type: "button",
          size: "sm",
          variant: "outline",
          className: "text-xs",
          disabled: loading,
          "aria-expanded": shown,
          onClick: shown ? () => setToken(null) : reveal,
          children: loading ? "Loading\u2026" : shown ? "Hide raw JWT" : "Show raw JWT"
        }
      ),
      shown && /* @__PURE__ */ jsx(CopyTextButton, { text: token ?? "" })
    ] }),
    shown && /* @__PURE__ */ jsx("pre", { className: "max-h-40 overflow-auto whitespace-pre-wrap break-all rounded-sm border bg-muted/40 p-2 font-mono text-2xs leading-relaxed", children: token || "(no access token)" })
  ] });
}
function DebugRowView({ label, value, copyable }) {
  const asCopyable = copyable && typeof value === "string" && value !== "" && value !== "\u2014";
  return /* @__PURE__ */ jsxs("div", { className: "flex items-start justify-between gap-3 text-xs", children: [
    /* @__PURE__ */ jsx("span", { className: "text-muted-foreground", children: label }),
    /* @__PURE__ */ jsx(
      "span",
      {
        className: "max-w-[60%] truncate font-mono text-foreground",
        title: typeof value === "string" ? value : void 0,
        children: asCopyable ? /* @__PURE__ */ jsx(CopyableValue, { value }) : value
      }
    )
  ] });
}
var SESSION_TAB = "session";
function DebugBlock({ title, children }) {
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(Separator, { className: "my-3" }),
    /* @__PURE__ */ jsx("p", { className: "mb-2 text-xs font-medium", children: title }),
    children
  ] });
}
function DebugResourceView({ entry }) {
  return /* @__PURE__ */ jsxs("div", { className: "space-y-3", children: [
    entry.id && /* @__PURE__ */ jsxs("div", { className: "flex items-start justify-between gap-3 text-xs", children: [
      /* @__PURE__ */ jsx("span", { className: "text-muted-foreground", children: "id" }),
      /* @__PURE__ */ jsx("span", { className: "max-w-[62%] truncate font-mono", children: /* @__PURE__ */ jsx(CopyableValue, { value: entry.id }) })
    ] }),
    /* @__PURE__ */ jsx(DebugFieldGroups, { groups: entry.groups })
  ] });
}
function DebugPanel({
  title = "Debug",
  rows = [],
  flagNames = [],
  variantFlags = [],
  sections = [],
  scopes,
  getAccessToken,
  onSignOut,
  federatedSignOut = false,
  showClearCache = true,
  enabled,
  defaultOpen = false
}) {
  const flags = useFeatureFlags();
  const [open, setOpen] = React3.useState(defaultOpen);
  const resources = useDebugResources();
  const [chosenTab, setChosenTab] = React3.useState(null);
  const tabExists = chosenTab === SESSION_TAB || resources.some((r) => r.resource === chosenTab);
  const tab = chosenTab && tabExists ? chosenTab : resources[0]?.resource ?? SESSION_TAB;
  const isOn = enabled ?? isDebugEnabled();
  if (!isOn) return null;
  const toggleFlag = (name, value) => {
    flags.setOverride(name, value);
    window.location.reload();
  };
  const forceVariant = (name, value) => {
    flags.setVariantOverride(name, value);
    window.location.reload();
  };
  if (!open) {
    return /* @__PURE__ */ jsx(
      Button,
      {
        type: "button",
        size: "icon",
        variant: "outline",
        className: "fixed bottom-4 right-4 z-50 rounded-full shadow-lg",
        onClick: () => setOpen(true),
        "aria-label": "Open debug panel",
        children: /* @__PURE__ */ jsx(Bug, { className: "size-4" })
      }
    );
  }
  const showSession = showClearCache || Boolean(onSignOut);
  const sessionPane = /* @__PURE__ */ jsxs(Fragment, { children: [
    rows.length > 0 && /* @__PURE__ */ jsx("div", { className: "space-y-1.5", children: rows.map((row, i) => /* @__PURE__ */ jsx(DebugRowView, { ...row }, `${row.label}-${i}`)) }),
    scopes && /* @__PURE__ */ jsx(DebugBlock, { title: `Scopes${scopes.length ? ` (${scopes.length})` : ""}`, children: /* @__PURE__ */ jsx(ScopesSection, { scopes }) }),
    /* @__PURE__ */ jsx(PlanSection, {}),
    getAccessToken && /* @__PURE__ */ jsx(DebugBlock, { title: "Raw JWT", children: /* @__PURE__ */ jsx(RawJwtSection, { getAccessToken }) }),
    showSession && /* @__PURE__ */ jsx(DebugBlock, { title: "Session", children: /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap gap-2", children: [
      showClearCache && /* @__PURE__ */ jsx(
        Button,
        {
          type: "button",
          size: "sm",
          variant: "outline",
          className: "text-xs",
          onClick: () => {
            clearAuthCache();
            window.location.reload();
          },
          children: "Clear local cache"
        }
      ),
      onSignOut && /* @__PURE__ */ jsx(
        Button,
        {
          type: "button",
          size: "sm",
          variant: "outline",
          className: "text-xs",
          onClick: () => onSignOut({ federated: false }),
          children: "Sign out"
        }
      ),
      onSignOut && federatedSignOut && /* @__PURE__ */ jsx(
        Button,
        {
          type: "button",
          size: "sm",
          variant: "outline",
          className: "text-xs",
          onClick: () => onSignOut({ federated: true }),
          children: "Federated (IdP too)"
        }
      )
    ] }) }),
    sections.map((section) => /* @__PURE__ */ jsx(DebugBlock, { title: section.title, children: section.content }, section.title)),
    variantFlags.length > 0 && /* @__PURE__ */ jsx(DebugBlock, { title: "Variants", children: /* @__PURE__ */ jsx("div", { className: "space-y-3", children: variantFlags.map(({ name, options }) => {
      const current = flags.variantOf(name);
      const layer = flags.variantLayerOf(name);
      return /* @__PURE__ */ jsxs("div", { className: "space-y-1.5 text-xs", "data-variant-flag": name, children: [
        /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between gap-2", children: [
          /* @__PURE__ */ jsx("span", { className: "font-mono text-muted-foreground", children: name }),
          /* @__PURE__ */ jsxs("span", { className: "text-muted-foreground", children: [
            current ?? "\u2014",
            layer ? ` (${layer})` : ""
          ] })
        ] }),
        /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap gap-1.5", children: [
          options.map((option) => /* @__PURE__ */ jsx(
            Button,
            {
              type: "button",
              size: "sm",
              variant: current === option ? "brand" : "outline",
              className: "text-xs",
              "aria-pressed": current === option,
              onClick: () => forceVariant(name, option),
              children: option
            },
            option
          )),
          layer === "override" && /* @__PURE__ */ jsx(
            Button,
            {
              type: "button",
              size: "sm",
              variant: "ghost",
              className: "text-xs",
              onClick: () => forceVariant(name, null),
              children: "Reset"
            }
          )
        ] })
      ] }, name);
    }) }) }),
    flagNames.length > 0 && /* @__PURE__ */ jsx(DebugBlock, { title: "Feature flags", children: /* @__PURE__ */ jsx("div", { className: "space-y-2", children: flagNames.map((name) => /* @__PURE__ */ jsxs("label", { className: "flex items-center justify-between text-xs", children: [
      /* @__PURE__ */ jsx("span", { className: "font-mono text-muted-foreground", children: name }),
      /* @__PURE__ */ jsx(Switch, { checked: flags.isEnabled(name), onCheckedChange: (v) => toggleFlag(name, v) })
    ] }, name)) }) })
  ] });
  return /* @__PURE__ */ jsxs("div", { className: "fixed bottom-4 right-4 z-50 flex max-h-[80vh] w-80 flex-col overflow-auto rounded-lg border bg-card p-4 text-card-foreground shadow-xl", children: [
    /* @__PURE__ */ jsxs("div", { className: "mb-3 flex items-center justify-between", children: [
      /* @__PURE__ */ jsxs("span", { className: "flex items-center gap-2 text-sm font-semibold", children: [
        /* @__PURE__ */ jsx(Bug, { className: "size-4" }),
        " ",
        title
      ] }),
      /* @__PURE__ */ jsx(Button, { type: "button", size: "icon", variant: "ghost", onClick: () => setOpen(false), "aria-label": "Close", children: /* @__PURE__ */ jsx(X, { className: "size-4" }) })
    ] }),
    resources.length === 0 ? sessionPane : /* @__PURE__ */ jsxs(Tabs, { value: tab, onValueChange: setChosenTab, children: [
      /* @__PURE__ */ jsxs(TabsList, { className: "mb-3 h-auto w-full flex-wrap justify-start", children: [
        /* @__PURE__ */ jsx(TabsTrigger, { value: SESSION_TAB, className: "px-2 py-0.5 text-xs", children: "Session" }),
        resources.map((entry) => /* @__PURE__ */ jsx(TabsTrigger, { value: entry.resource, className: "px-2 py-0.5 text-xs", children: resourceLabel(entry) }, entry.resource))
      ] }),
      /* @__PURE__ */ jsx(TabsContent, { forceMount: true, value: SESSION_TAB, className: "mt-0", children: sessionPane }),
      resources.map((entry) => /* @__PURE__ */ jsx(TabsContent, { forceMount: true, value: entry.resource, className: "mt-0", children: /* @__PURE__ */ jsx(DebugResourceView, { entry }) }, entry.resource))
    ] })
  ] });
}

export { CopyTextButton, CopyableValue, DebugFieldGroup, DebugFieldGroups, DebugPanel, DebugResourceProvider, DebugResourceRegistry, entrySignature, mergeDebugResources, resourceLabel, useCopy, useDebugResource, useDebugResources };
//# sourceMappingURL=chunk-BJRZH3GB.js.map
//# sourceMappingURL=chunk-BJRZH3GB.js.map